import { z } from 'zod';

import { defineEnvelope } from './envelope.js';

/**
 * Contratos de movimiento (TKT-013).
 *
 * Flujo:
 *   - Móvil → servidor: `player.move.intent` con dir normalizada + inputSeq monotónico.
 *   - Servidor → PC:   `entity.moved` por entidad afectada en cada tick relevante.
 *   - Servidor → PC:   `game.snapshot` con todas las entidades a SNAPSHOT_RATE_HZ.
 *
 * Sin combat, sin loot — sólo posición/velocidad/tickId.
 *
 * Refs ADR-0002 §"Cliente envía intents, nunca outcomes"; ADR-0003 §tick/broadcast.
 */

// Tolerancia para la refine de "magnitud ≤ 1" (errores de coma flotante del cliente).
const DIR_MAGNITUDE_EPSILON = 1e-3;

export const Vector2Schema = z
  .object({
    x: z.number().finite(),
    y: z.number().finite(),
  })
  .strict();

export type Vector2 = z.infer<typeof Vector2Schema>;

// ---------- player.move.intent ----------

export const MoveIntentPayloadSchema = z
  .object({
    /** Monotónico por jugador; servidor descarta duplicados o atrasados. */
    inputSeq: z.number().int().nonnegative(),
    /** Dirección normalizada; magnitud > 1 → servidor normaliza, no rechaza. */
    dir: Vector2Schema.refine((v) => Math.hypot(v.x, v.y) <= 1 + DIR_MAGNITUDE_EPSILON, {
      message: 'dir magnitude must be <= 1',
    }),
  })
  .strict();

export type MoveIntentPayload = z.infer<typeof MoveIntentPayloadSchema>;

export const MoveIntentTag = 'player.move.intent' as const;

export const MoveIntentEnvelopeSchema = defineEnvelope(MoveIntentTag, MoveIntentPayloadSchema);

// ---------- entity.moved ----------

export const EntityMovedPayloadSchema = z
  .object({
    // Acepta UUIDv7 de jugadores y IDs literales de enemigos (ej. "dummy-1").
    // Antes era .uuid() y rechazaba el dummy → snapshot entero descartado en
    // cliente y GameScene no pintaba nada (regresión de TKT-037).
    entityId: z.string().min(1).max(64),
    position: Vector2Schema,
    velocity: Vector2Schema,
    tickId: z.number().int().nonnegative(),
  })
  .strict();

export type EntityMovedPayload = z.infer<typeof EntityMovedPayloadSchema>;

export const EntityMovedTag = 'entity.moved' as const;

export const EntityMovedEnvelopeSchema = defineEnvelope(EntityMovedTag, EntityMovedPayloadSchema);

// ---------- game.snapshot ----------

export const GameSnapshotPayloadSchema = z
  .object({
    tickId: z.number().int().nonnegative(),
    /** Reloj autoritativo en epoca ms — el cliente lo usa para interpolación. */
    serverTime: z.number().int().nonnegative(),
    /**
     * Entidades controlables por jugadores. El cliente PC las pinta con paleta
     * de aliados. Mantenido como `entities` por compatibilidad con código previo
     * a TKT-037.
     */
    entities: z.array(EntityMovedPayloadSchema),
    /**
     * Enemigos del Coro (TKT-037). Pueden faltar (default array vacío) en
     * snapshots que no traigan mobs.
     */
    enemies: z.array(EntityMovedPayloadSchema).default([]),
  })
  .strict();

export type GameSnapshotPayload = z.infer<typeof GameSnapshotPayloadSchema>;

export const GameSnapshotTag = 'game.snapshot' as const;

export const GameSnapshotEnvelopeSchema = defineEnvelope(
  GameSnapshotTag,
  GameSnapshotPayloadSchema,
);
